<template>
  <div class="gallery-grid-images-section">
    <div id="gallery">
      <div class="container">
        <div
          v-if="section.header"
          class="row"
        >
          <div class="col-12">
            <h2 v-html="section.header" />
            <div
              class="sub-text"
              v-html="section.content"
            />
          </div>
        </div>
        <div class="gallery-holder">
          <div
            v-for="gallery in galleryWithIndices"
            :key="gallery.ID"
            class="grid-item"
          >
            <img
              :src="gallery.gallery_image.url"
              :alt="gallery.gallery_image.alt"
              loading="lazy"
              @click="openSlide(gallery.index_number)"
              @keypress="openSlide(gallery.index_number)"
            >
            <p v-html="gallery.caption" />
          </div>
        </div>
      </div>
    </div>

    <div
      id="slider"
      :class="{ 'yel-class': showSlider }"
    >
      <div class="container">
        <div
          class="close"
          @click="showSlider = false"
          @keypress="showSlider = false"
        >
          <svg
            data-v-35bcf9f0=""
            aria-hidden="true"
            focusable="false"
            data-prefix="fas"
            data-icon="xmark"
            role="img"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 384 512"
            class="svg-inline--fa fa-xmark fa-w-12"
          >
            <path
              data-v-35bcf9f0=""
              fill="currentColor"
              d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"
              class=""
            />
          </svg>
        </div>
        <VSlickCarousel
          ref="carousel"
          class="the-slider"
          :arrows="true"
          :dots="false"
          :center-mode="false"
          :adaptive-height="true"
          :variable-width="false"
          :groups-to-show="1"
          :groups-to-scroll="1"
          :focus-on-select="false"
        >
          <template #prevArrow="arrowOption">
            <div class="custom-arrow v-slick-arrow prev" @click="arrowOption.onClick">
              {{ arrowOption.currentSlideGroupIndex }}/{{ arrowOption.slideGroupCount }}
            </div>
          </template>
          <div
            v-for="gallery in galleryWithIndices"
            :key="gallery.ID"
          >
            <img
              :src="gallery.gallery_image.url"
              :alt="gallery.gallery_image.alt"
            >
            <p v-html="gallery.caption" />
          </div>
          <template #nextArrow="arrowOption">
            <div class="custom-arrow v-slick-arrow next" @click="arrowOption.onClick">
              {{ arrowOption.currentSlideGroupIndex }}/{{ arrowOption.slideGroupCount }}
            </div>
          </template>
        </VSlickCarousel>
      </div>
    </div>
  </div>
</template>

<script>
import RysEmbedModal from '@/components/modals/EmbedModal.vue';
import stripBlockTags from '@/utils/strip-block-tags';

export default {
  name: 'GalleryGridImagesSection',

  props: {
    section: { type: Object, required: true }
  },

  setup() {
    const modal = useModal();
    return { modal };
  },

  data() {
    return {
      showSlider: false,
      screenWidth: 0
    };
  },

  computed: {
    btnClass() {
      if (this.section.button_text) {
        if (this.section.style === 'fancy') {
          return 'black';
        }

        if (this.section.bg_color === 'white') {
          return 'ghost';
        }
      }

      return '';
    },

    imgXPos() {
      if (this.screenWidth >= 1170) {
        return this.section.img_x_pos;
      }
      return this.section.mob_img_x_pos;
    },

    imgYPos() {
      if (this.screenWidth >= 1170) {
        return this.section.img_y_pos;
      }
      return this.section.mob_img_y_pos;
    },

    galleryWithIndices() {
      return this.section.gallery.map((gallery, index) => ({
        ...gallery,
        index_number: index // 0-based index for Slick
      }));
    }
  },

  mounted() {
    window.addEventListener('resize', this.onWindowResize);
    this.onWindowResize();
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
  },

  methods: {
    stripBlockTags,

    showEmbedModal(html) {
      this.modal(RysEmbedModal, this, { props: { embed: html } });
    },

    openSlide(num) {
      this.showSlider = true;
      this.$nextTick(() => {
        if (this.$refs.carousel) {
          this.$refs.carousel.goTo(num);
        }
      });
    },

    onLinkClick(e, section) {
      if (section.link_shows_video && section.video) {
        e.preventDefault();
        this.showEmbedModal(section.video);
      }
    },

    onWindowResize() {
      this.screenWidth = window.innerWidth;
    }
  }
};
</script>

<style lang="scss" scoped>
@use 'sass:math';

div.split-image-section {
  position: relative;
  overflow-x: clip;

  .bg {
    display: none;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;

    .orange {
      position: relative;
      background-color: $thd-orange;
      height: 100%;
      width: 100%;
      transform: rotateZ(2deg) scale(110%);
      z-index: 2;

      @media screen and (min-width: $breakpoint-lg) {
        width: 50%;
        transform: rotateZ(2deg) scale(105%);
      }
    }

    .img {
      height: 100%;
      padding-top: 0;
      position: absolute;
      right: 0;
      width: 50%;
      z-index: 1;

      img {
        position: absolute;
        top: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
      }
    }
  }

  .container {
    position: relative;
    z-index: 2;
    display: grid;
    gap: 24px;
    grid-template-columns: minmax(auto, 100%);
    grid-template-rows: repeat(4, minmax(0, auto));

    @media screen and (min-width: $breakpoint-lg) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      grid-template-rows: repeat(2, auto) minmax(0, 1fr);
      grid-auto-flow: column dense;
      gap: 24px 60px;
    }

    .image-container,
    .video-container {
      @media screen and (min-width: $breakpoint-lg) {
        order: 9999;
        grid-row: span 3 / span 3;

        &.dir-left {
          order: -9999;
        }
      }

      .aspect {
        position: relative;
        width: 100%;
        padding-top: math.div(9, 16) * 100%;

        @media screen and (min-width: $breakpoint-lg) {
          padding-top: 100%;
        }

        > img {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;

          &.clickable {
            cursor: pointer;
          }
        }
      }

      .embed::v-deep {
        width: 100%;
        height: 0;
        overflow: hidden;
        position: relative;
        padding-top: math.div(9, 16) * 100%;

        iframe {
          width: 100% !important;
          height: 100% !important;
          position: absolute;
          top: 0;
          left: 0;
        }
      }
    }
  }

  &.bg-white {
    background-color: $thd-white;

    h2 {
      color: $thd-black;
    }

    .content {
      color: $thd-dark-gray;
    }
  }

  &.bg-black {
    background-color: $thd-black;

    h2 {
      color: $thd-white;
    }

    .content {
      color: $thd-white;
    }
  }

  &.style-fancy {
    margin-top: 64px;

    @media screen and (min-width: $breakpoint-lg) {
      margin-top: 0;
    }

    .bg {
      display: flex;

      &.dir-left {
        flex-direction: row-reverse;

        .orange {
          transform: rotateZ(-2deg) scale(110%);

          @media screen and (min-width: $breakpoint-lg) {
            transform: rotateZ(-2deg) scale(105%);
          }
        }

        .img {
          left: 0;
          right: unset;
        }
      }
    }

    .container::v-deep {
      padding-top: 32px;

      @media screen and (min-width: $breakpoint-lg) {
        padding-top: 60px;
      }

      h2 {
        color: $thd-white;
      }

      .content {
        color: $thd-white;

        strong,
        b {
          color: $thd-black;
        }
      }

      .image-container {
        @media screen and (min-width: $breakpoint-lg) {
          visibility: hidden;
        }
      }
    }
  }
}

#gallery {
  background-color: #efefef;

  .container {
    padding-top: 60px;
    padding-bottom: 60px;
    margin-top: 0;

    @media screen and (min-width: $breakpoint-md) {
      padding-bottom: 50px;
      margin-top: 0;
    }
  }

  .sub-text {
    margin-bottom: 40px;
    font-size: 20px;
  }

  .gallery-holder {
    display: grid;
    gap: 50px;
    grid-template-columns: repeat(1, 1fr);
    grid-auto-rows: 185px;

    @media screen and (min-width: $breakpoint-md) {
      gap: 50px 10px;
      grid-template-columns: repeat(3, 1fr);
      grid-auto-rows: 206px;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      cursor: pointer;
    }

    p {
      margin: 0;
    }
  }
}

#slider {
  display: none;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100vh;
  z-index: 9;
  background-color: rgb(0 0 0 / 75%);

  .container {
    position: fixed;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    color: #ffffff;

    .close {
      position: fixed;
      top: 25px;
      right: 25px;
      cursor: pointer;
      font-size: xx-large;
      z-index: 10;
      color: white;

      @media screen and (min-width: $breakpoint-md) {
        top: 104px;
      }
    }

    .the-slider {
      .v-slick-arrow {
        height: 50px !important;
        width: 50px !important;
        display: none;

        @media screen and (min-width: $breakpoint-md) {
          display: block !important;
        }
      }

      .v-slick-arrow.prev {
        left: -50px !important;
      }

      .v-slick-arrow.next {
        right: -50px !important;
      }

      .v-slick-arrow.prev::before,
      .v-slick-arrow.next::before {
        font-size: 50px !important;
      }

      .v-slick-slide-group {
        // Ensure slides take full width
        width: 100% !important;

        // Center content
        display: flex !important;
        flex-direction: column !important;
        align-items: center !important;
        justify-content: center !important;

        // Fix height issues
        height: auto !important;
        min-height: 40vh;

        @media screen and (min-width: $breakpoint-md) {
          min-height: 80vh;
        }
      }

      img {
        max-width: 90vw !important;
        max-height: 40vh !important;
        width: auto !important;
        height: auto !important;
        object-fit: contain !important; // Changed from cover
        margin: 0 auto !important; // Center images

        @media screen and (min-width: $breakpoint-md) {
          max-width: 100% !important;
          max-height: 85vh !important;
          margin: 0 auto !important;
        }
      }

      p {
        color: white;
        margin: 20px 0 0;
        font-size: 1.2rem;
        text-align: center;
        max-width: 90vw;

        @media screen and (min-width: $breakpoint-md) {
          font-size: 1.5rem;
          max-width: 80vw;
        }
      }

      .v-slick-dots {
        display: none !important;
      }
    }
  }
}

.yel-class {
  display: block !important;
}
</style>
